import {Box, Button, Typography} from '@mui/material'
import {t} from 'i18next'
import {ReactComponent as CloseIcon} from '../assets/close.svg'
import {AddressSearchBar} from './AddressSearchBar'
import {SuggestionsList} from './SuggestionsList'
import {EmptyState} from './EmptyState'
import {Suggestion} from '../hooks/useSearchAddress'
import {styles} from '../styles'

type Props = {
  searchValue: string
  onSearchChange: (value: string) => void
  suggestions: Suggestion[]
  isLoading: boolean
  isMarkerLoading: boolean
  onSuggestionSelect: (suggestion: Suggestion) => void
  canConfirm: boolean
  onConfirm: () => void
  onClearSelection?: () => void
  showResultList: boolean
  addressSnapped?: boolean
}

export const AddressSearch: React.FC<Props> = ({
  searchValue,
  onSearchChange,
  suggestions,
  isLoading,
  isMarkerLoading,
  onSuggestionSelect,
  canConfirm,
  onConfirm,
  onClearSelection,
  showResultList,
  addressSnapped
}) => {
  const showSuggestions = suggestions.length > 0 || isMarkerLoading || isLoading
  const showEmptyState = !showSuggestions && showResultList && !isMarkerLoading

  const onClear = () => {
    onClearSelection?.()
  }

  return (
    <Box sx={styles.searchContainer}>
      <AddressSearchBar
        value={searchValue}
        onChange={onSearchChange}
        data-test-id="address-search-bar"
      />
      {showSuggestions && (
        <Box sx={styles.suggestionsContainer} data-test-id="address-suggestions-container">
          {canConfirm && (
            <Box sx={styles.suggestionsListTile} data-test-id="address-selected-address-container">
              <Typography variant="h3" component="div" sx={{...styles.titleText, flexGrow: 1}}>
                {t('orderIntake.rmc.jobSiteModal.create.selectedAddress')}
              </Typography>
              <Button
                variant="text"
                onClick={onClear}
                sx={styles.clearSelectionButton}
                data-test-id="address-clear-selection-button"
              >
                <CloseIcon style={{width: 20, height: 20}} />
              </Button>
            </Box>
          )}
          <Box sx={styles.suggestionsLoadingContainer} data-test-id="address-loading-container">
            <SuggestionsList
              suggestions={suggestions}
              isLoading={isLoading || isMarkerLoading}
              onSuggestionSelect={onSuggestionSelect}
            />
          </Box>
          {canConfirm && (
            <Box data-test-id="address-confirm-button-container">
              <Button fullWidth variant="contained" onClick={onConfirm} sx={styles.confirmButton}>
                {t('orderIntake.rmc.jobSiteModal.create.confirmLocationButtonText')}
              </Button>
            </Box>
          )}
        </Box>
      )}
      {addressSnapped && (
        <Box
          sx={{...styles.suggestionsContainer, backgroundColor: '#668099'}}
          data-test-id="address-empty-state-container"
        >
          <Typography
            variant="h3"
            component="div"
            sx={{
              color: 'white',
              fontFamily: 'Inter',
              fontSize: 16,
              fontStyle: 'normal',
              fontWeight: 500,
              lineHeight: '24px'
            }}
          >
            {t('orderIntake.rmc.jobSiteModal.create.addressSnapped')}
          </Typography>
        </Box>
      )}
      {showEmptyState && (
        <Box sx={styles.suggestionsContainer} data-test-id="address-empty-state-container">
          <EmptyState />
        </Box>
      )}
    </Box>
  )
}
